import { AddAttachments } from "components/AddAttachments/AddAttachments";
import { RadioGroupField } from "components/controls/RadioGroupField";
import { TextareaField } from "components/controls/TextareaField";
import { ManageCategoryDocuments } from "components/ManageCategoryDocuments/ManageCategoryDocuments";
import { Grid } from "components/styled";
import { useFormikContext } from "formik";
import { helpAnchor } from "help/anchor";
import { useHandleConsentAutoenrollmentDependency } from "hooks/useHandleConsentAutoenrollmentDependency";
import { checkedOptions } from "library/constants";
import { ECheckedValue, IBenefitCategory, IRowItem } from "library/types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { UploadFile } from "../Upload/UploadFile";
import { BenefitCategoryNameField } from "./BenefitCategoryNameField";
import { useCanAddEditPhoto } from "hooks/useCanAddEditPhoto";

type Props = {
  benefitCategory?: IBenefitCategory;
  countryCode?: number | string;
  clientInternalNumberGOS?: number;
  rootCountryCode?: boolean;
};

export function BenefitFormItems({
  benefitCategory,
  countryCode,
  clientInternalNumberGOS,
  rootCountryCode,
}: Props) {
  const { t } = useTranslation();
  const { values, setFieldValue, errors } = useFormikContext<FormData>();
  const canAddEditPhoto = useCanAddEditPhoto();
  const consentsNeeded = values["consentsNeeded"] as ECheckedValue;

  useHandleConsentAutoenrollmentDependency();

  return (
    <Container>
      <Grid>
        <BenefitCategoryNameField
          isAddCategory={!benefitCategory}
          countryCode={countryCode}
          clientInternalNumberGOS={clientInternalNumberGOS}
          rootCountryCode={rootCountryCode}
        />
        <RadioGroupField
          data-help="benefit-category-dialog-consent"
          value={values["consentsNeeded"]}
          setFieldValue={setFieldValue}
          name="consentsNeeded"
          label={t("hbh.consentNeeded.label")}
          options={checkedOptions}
          // required
        />
        <RadioGroupField
          data-help="benefit-category-dialog-auto-enrollment"
          disabled={consentsNeeded === ECheckedValue.Yes}
          value={values["isAutoEnrollment"]}
          setFieldValue={setFieldValue}
          name="isAutoEnrollment"
          label={t("hbh.isAutoEnrollment.label")}
          options={checkedOptions}
          // required
        />
        <UploadFile
          dataHelp={helpAnchor("benefit-category-dialog-cover")}
          disabled={!canAddEditPhoto}
          isUploadAppImages
          key={values["coverPhoto"]?.path}
          setFieldValue={setFieldValue}
          fieldName="coverPhoto"
          defaultValue={values["coverPhoto"]}
          label={canAddEditPhoto ? t("hbh.chooseCoverPicture.label") : t("hbh.coverPicture.label")}

        />
      </Grid>
      <Grid $gridColumns={1}></Grid>
      <TextareaField
        data-help="benefit-category-dialog-description"
        required
        label={t("hbh.description.label")}
        name="description"
        value={values["description"]}
        setFieldValue={setFieldValue}
        error={errors["description"]}
        rows={3}
        resize="vertical"
      />
      {benefitCategory ? (
        <ManageCategoryDocuments
          item={benefitCategory as IRowItem}
          dataHelp={helpAnchor("benefit-category-dialog-documents")}
        />
      ) : (
        <AddAttachments
          attachHiddenForEmployee
          dataHelp={helpAnchor("benefit-category-dialog-attachments")}
        />
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
`;
